import { Chain, ESortChainsType, Timeframe } from '@ankr.com/chains-list';
import { useMemo } from 'react';

import { BlockchainsCountsMap } from 'modules/stats/types';
import {
  formatRequestsCount,
  sortPublicChains,
} from 'domains/chains/screens/ChainsListPage/components/PublicChains/hooks/utils';
import { getFilteredChainsByName } from 'modules/common/utils/getFilteredChainsByName';
import { selectPublicAggregatedBlockchainsCountsByChainID } from 'modules/stats/store/public/selectors';
import { sortPrivateChains } from 'domains/chains/screens/ChainsListPage/components/PrivateChains/utils/sortPrivateChains';
import { useAppSelector } from 'store/useAppSelector';
import { useChainsPublicStats } from 'domains/chains/screens/ChainsListPage/components/PublicChains/hooks/useChainsPublicStats';

import { isMultichain } from '../utils/isMultichain';

export interface ChainsParams {
  chains: Chain[];
  sortType: ESortChainsType;
  timeframe: Timeframe;
  searchContent: string;
  blockchainsCountsMap?: BlockchainsCountsMap;
  isPublicChainsChecked: boolean;
}

export const useChainsSorting = ({
  blockchainsCountsMap,
  chains,
  isPublicChainsChecked,
  searchContent,
  sortType,
  timeframe,
}: ChainsParams) => {
  const filteredBySearchChains = useMemo(
    () =>
      chains?.filter(chain =>
        getFilteredChainsByName(chain, searchContent.toLowerCase()),
      ),
    [chains, searchContent],
  );

  const { loading: arePublicStatsLoading, params } = useChainsPublicStats({
    timeframe,
  });

  const publicStats = useAppSelector(state =>
    selectPublicAggregatedBlockchainsCountsByChainID(state, params),
  );

  const sortedChainsByPublicUsage = useMemo(() => {
    return sortPublicChains({
      chains: formatRequestsCount(filteredBySearchChains, publicStats),
      sortType: ESortChainsType.UsageHighLow,
      isLoading: arePublicStatsLoading,
    });
  }, [arePublicStatsLoading, publicStats, filteredBySearchChains]);

  const sortedChainsByPrivateUsage = useMemo(() => {
    if (!blockchainsCountsMap) {
      return sortedChainsByPublicUsage;
    }

    return sortPrivateChains({
      blockchainsCountsMap,
      chains: sortedChainsByPublicUsage,
      sortType: ESortChainsType.UsageHighLow,
    });
  }, [blockchainsCountsMap, sortedChainsByPublicUsage]);

  const sortedChains = useMemo(() => {
    if (sortType === ESortChainsType.NameAZ) {
      return [...filteredBySearchChains].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    }

    if (sortType === ESortChainsType.NameZA) {
      return [...filteredBySearchChains].sort((a, b) =>
        b.name.localeCompare(a.name),
      );
    }

    // check if there any info in requestsData
    const hasPrivateStats =
      blockchainsCountsMap && Object.values(blockchainsCountsMap).length > 0;

    if (hasPrivateStats) {
      if (sortType === ESortChainsType.UsageHighLow) {
        return sortedChainsByPrivateUsage;
      }

      if (sortType === ESortChainsType.UsageLowHigh) {
        return sortedChainsByPrivateUsage.slice().reverse();
      }
    }

    return sortedChainsByPublicUsage;
  }, [
    blockchainsCountsMap,
    filteredBySearchChains,
    sortType,
    sortedChainsByPrivateUsage,
    sortedChainsByPublicUsage,
  ]);

  const filteredChains = useMemo(() => {
    if (!isPublicChainsChecked) return sortedChains;

    return sortedChains.filter(
      chain => chain.isPublic && !isMultichain(chain.id),
    );
  }, [isPublicChainsChecked, sortedChains]);

  return { processedChains: filteredChains };
};
